import React, { useEffect, useState } from 'react';

interface LoadingOverlayProps {
  isOpen: boolean;
  message?: string;
  successMessage?: string;
  duration?: number;
  status?: 'loading' | 'success' | 'error';
  onSuccessClose?: () => void;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isOpen,
  message = 'Carregando...',
  successMessage = 'Concluído!',
  duration = 1500,
  status = 'loading',
  onSuccessClose
}) => {
  const [progress, setProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setProgress(0);
      setShowSuccess(false);
      return;
    }

    if (status === 'success') {
      setProgress(100);
      setShowSuccess(true);
      const timeout = setTimeout(() => {
        if (onSuccessClose) onSuccessClose();
      }, 1800);
      return () => clearTimeout(timeout);
    }

    if (status === 'error') {
      setProgress(100);
      setShowSuccess(false);
      return;
    }

    setProgress(0);
    setShowSuccess(false);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percentage = Math.min((elapsed / duration) * 100, 95);
      setProgress(percentage);
    }, 50);

    return () => clearInterval(interval);
  }, [isOpen, duration, status, onSuccessClose]);

  if (!isOpen) return null;

  const isSuccess = status === 'success' || showSuccess;
  const isError = status === 'error';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className={`bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 transition-all duration-300 ${isSuccess ? 'ring-2 ring-green-400' : isError ? 'ring-2 ring-red-400' : ''}`}>
        <div className="flex flex-col items-center">
          {/* Spinner / Success / Error */}
          {isSuccess ? (
            <div className="mb-6 animate-[bounce_0.5s_ease-in-out]">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-green-100 animate-[ping_0.8s_ease-out]"></div>
                <div className="absolute inset-0 rounded-full bg-green-500 opacity-20"></div>
                <svg className="w-12 h-12 text-green-600 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" className="animate-[dash_0.5s_ease-in-out_forwards]" style={{ strokeDasharray: 30, strokeDashoffset: 0 }} />
                </svg>
              </div>
            </div>
          ) : isError ? (
            <div className="mb-6">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-red-100"></div>
                <div className="absolute inset-0 rounded-full bg-red-500 opacity-20"></div>
                <svg className="w-12 h-12 text-red-600 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          ) : (
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-full border-4 border-gray-200"></div>
              <div className="absolute top-0 left-0 w-16 h-16 rounded-full border-4 border-transparent border-t-blue-600 animate-spin"></div>
            </div>
          )}

          {/* Message */}
          <p className={`font-medium text-lg mb-4 text-center ${isSuccess ? 'text-green-600' : isError ? 'text-red-600' : 'text-gray-700'}`}>
            {isSuccess ? successMessage : isError ? 'Erro!' : message}
          </p>

          {/* Progress bar */}
          {!isSuccess && !isError && (
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300 ease-out flex items-center justify-end pr-1"
                style={{ width: `${progress}%` }}
              >
                {progress > 10 && (
                  <span className="text-[10px] font-bold text-white">
                    {Math.round(progress)}%
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Success bar */}
          {isSuccess && (
            <div className="w-full bg-green-100 rounded-full h-3 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500 flex items-center justify-end pr-1" style={{ width: '100%' }}>
                <span className="text-[10px] font-bold text-white">100%</span>
              </div>
            </div>
          )}

          {/* Status text */}
          {!isSuccess && !isError && (
            <p className="text-gray-400 text-xs mt-3">
              {progress < 100 ? 'Processando...' : 'Concluído!'}
            </p>
          )}
          {isSuccess && (
            <p className="text-green-400 text-xs mt-3 font-medium">
              ✓ Operação realizada com sucesso!
            </p>
          )}
          {isError && (
            <p className="text-red-400 text-xs mt-3">
              Tente novamente em instantes.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingOverlay;
